import { useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listLabels,
  listLabelGroups,
  createLabel,
  createLabelGroup,
  updateLabel,
  deleteLabel,
  ApiError,
  updateLabelGroup,
  getMe,
  listProjects,
  listMembers,
  type Label,
} from '../api';
import { Select } from './Select';
import { roleAllows } from '../roles';
import { ColorPicker } from './ColorPicker';
import { QueryState } from './QueryState';
import { Icon } from './Icon';

/**
 * Labels, and the groups that own them.
 *
 * A group is a question — "what kind of work is this?" — and its labels are the answers. Exclusive
 * by default, which is the property that makes a composition read as a whole rather than as
 * overlapping tallies.
 *
 * The screen is deliberately plain. Its job is to let you set up an axis once and then never come
 * back: the work happens in the pickers on capture and on a task, not here.
 */

/** Labels arranged under their parents, for display only — the rollup itself is report-time. */
function tree(all: Label[], parentId: string | null = null): { label: Label; depth: number }[] {
  return all
    .filter((l) => l.parentLabelId === parentId)
    .flatMap((l) => [
      { label: l, depth: 0 },
      ...tree(all, l.id).map((c) => ({ ...c, depth: c.depth + 1 })),
    ]);
}

function LabelRow({
  label,
  depth,
  all,
  editable,
}: {
  label: Label;
  depth: number;
  all: Label[];
  /** False on a shared group this caller may read but not change — see `canEdit`. */
  editable: boolean;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const invalidate = () => queryClient.invalidateQueries();

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => updateLabel(label.id, body),
    onSuccess: invalidate,
  });
  const remove = useMutation({ mutationFn: () => deleteLabel(label.id), onSuccess: invalidate });

  // A label cannot be its own parent, nor its own descendant; the service refuses both, so those
  // are removed here rather than offered and then rejected.
  const descendants = new Set(tree(all, label.id).map((n) => n.label.id));
  const parentOptions = all
    .filter((l) => l.id !== label.id && !descendants.has(l.id))
    .map((l) => ({ value: l.id, label: l.name }));

  return (
    <li className="label-row" data-testid="label-row" data-depth={depth}>
      <span className={`swatch ${label.color ? `goal-c-${label.color}` : ''}`} />
      <span className="label-name">{label.name}</span>
      {editable && (
        <span className="task-meta">
          <button className="link" onClick={() => setEditing((v) => !v)}>
            {editing ? 'done' : 'edit'}
          </button>
        </span>
      )}

      {editable && editing && (
        <div className="label-edit">
          <label>
            Rolls up into
            <Select
              options={parentOptions}
              value={label.parentLabelId}
              onChange={(next) => save.mutate({ parentLabelId: next })}
              placeholder="Nothing — a top-level label"
              emptyLabel="Nothing — a top-level label"
              ariaLabel={`Parent of ${label.name}`}
            />
          </label>
          <ColorPicker
            name={`label-color-${label.id}`}
            label="Colour"
            value={label.color}
            onChange={(color) => save.mutate({ color })}
          />
          <button
            /*
              `danger`, not `bad`. `styles.css` has `.link.danger` and `button.danger:not(.link)` and
              has never had a `.link.bad` rule, so this fell through `.link`'s `color: inherit` and came
              out the same dark as the `done` beside it — the app owning a destructive red and not
              spending it here (`01a08097-0e4c`). One spelling: `Templates.tsx` already uses `danger`
              for the same act, and an alias rule would have made two.
            */
            className="link danger"
            onClick={() => remove.mutate()}
            disabled={remove.isPending}
            aria-label={`Delete ${label.name}`}
          >
            delete
          </button>
          <p className="subtext">
            Deleting lifts any labels under this one to its own parent, rather than orphaning them.
          </p>
        </div>
      )}
    </li>
  );
}

export function Labels() {
  const queryClient = useQueryClient();
  const groups = useQuery({ queryKey: ['label-groups'], queryFn: listLabelGroups });
  const all = useQuery({ queryKey: ['labels'], queryFn: listLabels });

  /*
    The projects this group could be shared with — the ones the caller may actually write to.

    Only `maintainer` and `owner` are offered, because those are the only roles `createLabelGroup`
    accepts. Offering the others and then showing a 403 is the "offered and then rejected" shape the
    parent picker on a label row deliberately avoids, and a reader has no way to tell a mis-click
    from a broken app.

    The role is not on `listProjects` — `ProjectProgress.members` is a count — so it comes from
    membership, per project, the way `useAssignees` derives the same answer. Cached and rarely
    changing, and this screen renders a handful of times per session.
  */
  const me = useQuery({ queryKey: ['me'], queryFn: getMe, retry: false });
  const projects = useQuery({ queryKey: ['projects'], queryFn: listProjects });
  const memberships = useQueries({
    queries: (projects.data ?? []).map((p) => ({
      queryKey: ['members', p.id],
      queryFn: () => listMembers(p.id),
      staleTime: 60_000,
    })),
  });
  /** A project's title for the pill, falling back to its id while the list is still loading. */
  const projectName = (id: string) =>
    (projects.data ?? []).find((p) => p.id === id)?.title ?? 'a project';
  /** The caller's accepted role on a project, or undefined if they are not a member of it. */
  const roleOn = (projectId: string) => {
    const i = (projects.data ?? []).findIndex((p) => p.id === projectId);
    if (i < 0) return undefined;
    const mine = (memberships[i]?.data ?? []).find((m) => m.userId === me.data?.id);
    return mine?.acceptedAt === null ? undefined : mine?.role;
  };
  const shareable = (projects.data ?? []).filter((_project, i) => {
    const mine = (memberships[i]?.data ?? []).find((m) => m.userId === me.data?.id);
    return mine?.acceptedAt !== null && roleAllows(mine?.role, 'write');
  });
  /*
    Whether this caller may change the group, as opposed to read it.

    `listLabelGroups` has returned every group the caller can *reach* since Phase 63 — their own
    and every project's they are an accepted member of — and this screen rendered the maintainer's
    toolbar over all of them. Only the share picker asked about the role. So a reporter on a shared
    project saw rename, recolour, delete, the exclusivity pill and an add form on the project's
    taxonomy, and got a 403 banner after every press: the service refuses all of it below `write`
    (`authorizeLabelWrite`, whose comment explains why a reporter must not be able to rename a
    team's taxonomy).

    A member who cannot tell which controls are theirs learns to distrust all of them. The ladder
    is the service's, unchanged — read at `reporter`, apply at `developer`, edit at `maintainer` —
    and this is the screen adopting it rather than a second copy of it.
  */
  const canEdit = (group: { projectId: string | null }) =>
    group.projectId === null || roleAllows(roleOn(group.projectId), 'write');
  const [groupName, setGroupName] = useState('');
  const [exclusive, setExclusive] = useState(true);
  /** The project to share the new group with, or null for a personal one. */
  const [groupProject, setGroupProject] = useState<string | null>(null);
  const setExclusivity = useMutation({
    mutationFn: (group: { id: string; exclusive: boolean }) =>
      updateLabelGroup(group.id, { exclusive: !group.exclusive }),
    onSuccess: () => queryClient.invalidateQueries(),
  });
  const setSharing = useMutation({
    mutationFn: ({ id, projectId }: { id: string; projectId: string | null }) =>
      updateLabelGroup(id, { projectId }),
    onSuccess: () => queryClient.invalidateQueries(),
  });
  const [newLabel, setNewLabel] = useState<Record<string, string>>({});

  const invalidate = () => queryClient.invalidateQueries();
  const addGroup = useMutation({
    mutationFn: () =>
      createLabelGroup({
        name: groupName.trim(),
        exclusive,
        /* Omitted rather than sent as null when nothing is chosen: a personal group is the default
           and the field is what makes it otherwise. */
        ...(groupProject ? { projectId: groupProject } : {}),
      }),
    onSuccess: () => {
      setGroupName('');
      setGroupProject(null);
      invalidate();
    },
  });
  const addLabel = useMutation({
    mutationFn: ({ groupId, name }: { groupId: string; name: string }) =>
      createLabel({ groupId, name }),
    onSuccess: invalidate,
  });

  return (
    <section>
      <h2>Labels</h2>
      {/*
        Explained once, to somebody who has not seen it work.

        This paragraph and the "No groups yet" line below both explained what a label is, so an
        empty account read the same idea twice and an account with forty labels read it at all —
        to a person who plainly already knows. It is onboarding copy, so it goes when there is
        something on the screen to look at instead.
      */}
      {(groups.data?.length ?? 0) === 0 && (
        <p className="muted">
          A group is a question; its labels are the answers. Exclusive groups allow one answer per
          task — picking a second replaces the first, which is what lets you read a composition.
        </p>
      )}

      <form
        className="inline-form new-group-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (groupName.trim()) addGroup.mutate();
        }}
      >
        <input
          value={groupName}
          placeholder="New group — kind of work, energy, place…"
          aria-label="New group"
          onChange={(e) => setGroupName(e.target.value)}
        />
        <label className="check">
          <input
            type="checkbox"
            checked={exclusive}
            onChange={(e) => setExclusive(e.target.checked)}
          />
          One answer only
        </label>
        {/*
          Whose taxonomy this is, at the moment it is created.

          The route has accepted `projectId` since migration 0026 and this form never sent it, so
          the one screen whose job is "set up an axis once and never come back" could not set up a
          project's axis at all — the answer was to use a REST client.

          Absent entirely when the caller has no project they may write to, which is most accounts:
          a disabled picker offering nothing is furniture explaining a feature you cannot use.
        */}
        {shareable.length > 0 && (
          <Select
            compact
            options={shareable.map((p) => ({ value: p.id, label: p.title }))}
            value={groupProject}
            onChange={setGroupProject}
            placeholder="Just mine"
            emptyLabel="Just mine"
            ariaLabel="Share the group with a project"
          />
        )}
        <button type="submit" disabled={!groupName.trim() || addGroup.isPending}>
          Add group
        </button>
      </form>
      {addGroup.error instanceof ApiError && <p className="banner">{addGroup.error.message}</p>}

      <QueryState query={groups} />
      {groups.data?.length === 0 && (
        <p className="muted">Add one above and it becomes a picker on every task.</p>
      )}

      {groups.data?.map((group) => {
        const mine = (all.data ?? []).filter((l) => l.groupId === group.id);
        const editable = canEdit(group);
        return (
          <div className="card" key={group.id} data-testid="label-group">
            <h3>
              {group.name}{' '}
              {/*
                The badge is a control now.

                `exclusive` was fixed at creation with no route to change it, which is tolerable
                for a taxonomy one person owns and wrong for one a team shares — a shared
                classification gets got slightly wrong and then needs correcting. Correcting it on
                the design backlog needed a direct `UPDATE` against the database.

                Turning it *on* over data that violates it is refused, with the count named, so
                the message is worth showing rather than swallowing: silently dropping somebody's
                filing is the worse failure, and the person who loses it is not the one clicking.
              */}
              {/* A statement rather than a control when it is not yours to press — the fact still
                  matters to a reader filing a task under it. */}
              {editable ? (
                <button
                  className="pill link-pill"
                  data-testid="toggle-exclusive"
                  title={
                    group.exclusive
                      ? 'One answer per task. Press to allow several.'
                      : 'Several allowed. Press to require one answer.'
                  }
                  onClick={() => setExclusivity.mutate(group)}
                  disabled={setExclusivity.isPending}
                >
                  {group.exclusive ? 'one answer' : 'several allowed'}
                </button>
              ) : (
                <span className="pill">{group.exclusive ? 'one answer' : 'several allowed'}</span>
              )}{' '}
              {/*
                Whose taxonomy this is — named, and a control, for the reason the badge beside it
                already is.

                This read `shared`, with a title saying "Shared with the project". Which project was
                never said and an account can be in several, so a group shared with the wrong one
                read exactly like a group shared with the right one — and there was no way to fix it
                here. That is the `exclusive` badge before Phase 58, whose comment records what the
                alternative cost: "correcting it on the design backlog needed a direct UPDATE
                against the database".

                Only the creator gets the take-back control. The service refuses everybody else,
                because unsharing moves the group into the creator's personal list and out of the
                actor's own reach — and a control that always refuses is worse than none.
              */}
              {group.projectId ? (
                group.userId === me.data?.id ? (
                  <button
                    className="pill link-pill"
                    data-testid="shared-group"
                    title={`Shared with ${projectName(group.projectId)}. Press to take it back — its labels stay on the project’s tasks as yours alone.`}
                    onClick={() => setSharing.mutate({ id: group.id, projectId: null })}
                    disabled={setSharing.isPending}
                  >
                    shared with {projectName(group.projectId)}
                  </button>
                ) : (
                  <span
                    className="pill"
                    data-testid="shared-group"
                    title={`Shared with ${projectName(group.projectId)}. Only whoever created it can take it back.`}
                  >
                    shared with {projectName(group.projectId)}
                  </span>
                )
              ) : (
                shareable.length > 0 && (
                  <Select
                    compact
                    options={shareable.map((p) => ({ value: p.id, label: p.title }))}
                    value={null}
                    onChange={(next) =>
                      next && setSharing.mutate({ id: group.id, projectId: next })
                    }
                    placeholder="Share with…"
                    ariaLabel={`Share ${group.name} with a project`}
                  />
                )
              )}
            </h3>
            {setExclusivity.isError && setExclusivity.variables?.id === group.id && (
              <p className="banner" data-testid="exclusive-refused">
                {(setExclusivity.error as Error).message}
              </p>
            )}
            {/*
              The share refusal, shown rather than swallowed — the same treatment
              `exclusive-refused` gets, and for the same reason: it names how many tasks are in the
              way, which is what makes it actionable.
            */}
            {setSharing.isError && setSharing.variables?.id === group.id && (
              <p className="banner" data-testid="share-refused">
                {(setSharing.error as Error).message}
              </p>
            )}

            <ul className="plain label-list">
              {tree(mine).map(({ label, depth }) => (
                <LabelRow
                  key={label.id}
                  label={label}
                  depth={depth}
                  all={all.data ?? []}
                  editable={editable}
                />
              ))}
            </ul>
            {mine.length === 0 && <p className="muted">No labels in this group yet.</p>}

            {/* Read-only, and said so — the labels are listed above, which is the point. Hiding the
                group would be worse than showing it without controls: the plan is what a member
                came here to read. */}
            {!editable && (
              <p className="muted" data-testid="read-only-group">
                Managed by the project’s maintainers.
              </p>
            )}

            {editable && (
              <form
                className="inline-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  const name = (newLabel[group.id] ?? '').trim();
                  if (!name) return;
                  addLabel.mutate({ groupId: group.id, name });
                  setNewLabel((m) => ({ ...m, [group.id]: '' }));
                }}
              >
                <input
                  value={newLabel[group.id] ?? ''}
                  placeholder="Add a label"
                  aria-label={`Add a label to ${group.name}`}
                  onChange={(e) => setNewLabel((m) => ({ ...m, [group.id]: e.target.value }))}
                />
                <button type="submit" aria-label={`Add to ${group.name}`}>
                  <Icon name="caret" size={14} />
                </button>
              </form>
            )}
          </div>
        );
      })}
    </section>
  );
}
